import { useCallback, useEffect, useState } from "react";
import { db } from "firebaseConfig";
import useAuth from "./useAuth";
import { USER_FEEDBACKS_COLLECTION_NAME } from "../@types/sharedSchema";

/**
 * "Has this user already submitted feedback tagged `featureTag`?"
 *
 * The `user-feedbacks` collection is the single record of a registration /
 * waitlist signup — there is no duplicate marker on the user doc. This hook is
 * how a promo banner reads that back so it can switch to its "you're
 * registered" state instead of asking again.
 *
 * The query is `userId == me && featureTag == tag`, which is also exactly what
 * the Firestore rule permits (`allow read: if resource.data.userId ==
 * getUserMail()`). Two equality filters need no composite index.
 */

export interface SubmittedFeedbackState {
  /** The lookup finished (or was skipped because there's no signed-in user). */
  readonly isLoaded: boolean;
  /** A feedback doc with this tag exists for the current user. */
  readonly hasSubmitted: boolean;
  /**
   * Mark as submitted without waiting for a re-query — call right after a
   * successful write so the UI flips immediately.
   */
  readonly markSubmitted: () => void;
}

export function useHasSubmittedFeedback(featureTag: string): SubmittedFeedbackState {
  const { user } = useAuth();
  const userEmail = user?.email;

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!userEmail) {
      setIsLoaded(true);
      return;
    }

    // One-shot read, not a listener: a submission always happens through this
    // component, which flips the flag itself via markSubmitted().
    let cancelled = false;
    setIsLoaded(false);
    db.collection(USER_FEEDBACKS_COLLECTION_NAME)
      .where("userId", "==", userEmail)
      .where("featureTag", "==", featureTag)
      .limit(1)
      .get()
      .then((snapshot) => {
        if (cancelled) return;
        setHasSubmitted(!snapshot.empty);
        setIsLoaded(true);
      })
      .catch((error) => {
        // Failing open (banner keeps asking) is the safe direction — worst case
        // the user registers twice, which is far better than silently hiding a
        // live campaign because one read failed.
        console.error(`Failed to look up feedback for tag ${featureTag}`, error);
        if (!cancelled) setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail, featureTag]);

  const markSubmitted = useCallback(() => setHasSubmitted(true), []);

  return { isLoaded, hasSubmitted, markSubmitted };
}

export default useHasSubmittedFeedback;
